// A DICOM listener can be limited to the hosts allowed to send to it. The
// portal collects them as one comma-separated text field, so this turns that
// text into the list the hub expects and reports any entry that is not an IP
// address or CIDR range, rather than silently dropping it.

export type ParsedAllowedRemoteHostsType = {
  hosts: Array<string>;
  invalid: Array<string>;
};

// One octet, 0-255. Leading zeros are rejected: "010" reads as octal to some
// parsers and decimal to others, so it can't be trusted to mean one address.
const IPV4_OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
const IPV4_PATTERN = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`);

const IPV4_MAX_PREFIX = 32;
const IPV6_MAX_PREFIX = 128;

const isIpv4 = (value: string): boolean => IPV4_PATTERN.test(value);

// IPv6 has too many valid spellings (compressed zeros, embedded IPv4) for a
// readable regex, so the URL parser, which implements the spec, decides.
const isIpv6 = (value: string): boolean => {
  if (!value.includes(":") || !/^[0-9a-fA-F:.]+$/.test(value)) return false;
  try {
    new URL(`http://[${value}]`);
    return true;
  } catch {
    return false;
  }
};

const isValidPrefix = (prefix: string, maxPrefix: number): boolean =>
  /^\d{1,3}$/.test(prefix) && Number(prefix) <= maxPrefix;

export const isValidRemoteHost = (entry: string): boolean => {
  const [address, prefix, ...rest] = entry.split("/");
  if (rest.length > 0) return false;

  if (isIpv4(address)) {
    return prefix === undefined || isValidPrefix(prefix, IPV4_MAX_PREFIX);
  }
  if (isIpv6(address)) {
    return prefix === undefined || isValidPrefix(prefix, IPV6_MAX_PREFIX);
  }
  return false;
};

// Splits on commas, trims each entry and skips empty ones, so a trailing comma
// or extra spaces aren't errors. Duplicates are kept once, in first-seen order.
export const parseAllowedRemoteHosts = (
  input: string
): ParsedAllowedRemoteHostsType => {
  const entries = Array.from(
    new Set(
      input
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
    )
  );

  return {
    hosts: entries.filter(isValidRemoteHost),
    invalid: entries.filter((entry) => !isValidRemoteHost(entry)),
  };
};
